import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import CommonWrapper from '../../../components/ui_components/FounderWrapper';
import colors from '../../../styles/colors';
import { apiGetCards } from '../../../api/cardApi';
import { apiGetExpansions } from '../../../api/expansionApi';
import { apiGetPacks } from '../../../api/packApi';
import { apiGetCardAccounts } from '../../../api/userApi';
import { apiGetLeaderboard } from '../../../api/userCardApi';
import type { Card, CardRarity } from '../../../types/models/card';
import type { Expansion } from '../../../types/models/expansion';
import type { Pack } from '../../../types/models/pack';
import type { CardAccount } from '../../../types/models/userCredit';
import type { LeaderboardEntry } from '../../../api/userCardApi';

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageInner = styled.div`
  padding: 32px 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: gold;
  margin: 0 0 32px;
`;

const Section = styled.div`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin: 0 0 16px;
`;

// ─── KPI Grid ─────────────────────────────────────────────────────────────────

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const KpiCard = styled.div<{ $accent?: string }>`
  background-color: #1a1a1a;
  border: 1px solid ${({ $accent }) => $accent ? `${$accent}33` : 'rgba(255,255,255,0.08)'};
  border-radius: 10px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const KpiLabel = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const KpiValue = styled.span<{ $accent?: string }>`
  font-size: 32px;
  font-weight: 800;
  color: ${({ $accent }) => $accent ?? '#fff'};
  line-height: 1;
`;

const KpiSub = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  margin-top: 2px;
`;

// ─── Rarity Distribution ──────────────────────────────────────────────────────

const RarityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const RarityCard = styled.div<{ $color: string }>`
  background-color: #1a1a1a;
  border: 1px solid ${({ $color }) => $color}33;
  border-radius: 8px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RarityName = styled.span<{ $color: string }>`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  text-transform: capitalize;
`;

const RarityCount = styled.span`
  font-size: 28px;
  font-weight: 800;
  color: #fff;
  line-height: 1;
`;

const RarityPct = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
`;

const RarityTrack = styled.div`
  height: 4px;
  background-color: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 4px;
`;

const RarityFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background-color: ${({ $color }) => $color};
  border-radius: 2px;
`;

// ─── Top Collectors ───────────────────────────────────────────────────────────

const CollectorsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  max-width: 700px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 14px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
`;

const ThRight = styled(Th)`
  text-align: right;
`;

const Tr = styled.tr`
  &:hover td { background-color: rgba(255,255,255,0.02); }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
`;

const TdRight = styled(Td)`
  text-align: right;
`;

const RankBadge = styled.span<{ $rank: number }>`
  font-weight: 700;
  color: ${({ $rank }) => $rank === 1 ? 'gold' : $rank === 2 ? '#C0C0C0' : $rank === 3 ? '#cd7f32' : 'rgba(255,255,255,0.3)'};
`;

const LegBadge = styled.span`
  color: gold;
  font-size: 12px;
  font-weight: 600;
`;

const StatusText = styled.p`
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
`;

// ─── Credits Section ──────────────────────────────────────────────────────────

const CreditsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  max-width: 500px;
`;

// ─── Rarity Colors ────────────────────────────────────────────────────────────

const RARITY_COLOR: Record<CardRarity, string> = {
  common: 'rgba(255,255,255,0.55)',
  rare: '#4a9eff',
  epic: '#c27ff5',
  legendary: 'gold',
};

const RARITIES: CardRarity[] = ['common', 'rare', 'epic', 'legendary'];

// ─── Component ────────────────────────────────────────────────────────────────

const DashboardPage: React.FC = () => {
  const [cards, setCards] = useState<Card[]>([]);
  const [expansions, setExpansions] = useState<Expansion[]>([]);
  const [packs, setPacks] = useState<Pack[]>([]);
  const [accounts, setAccounts] = useState<CardAccount[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    Promise.all([
      apiGetCards().catch(() => []),
      apiGetExpansions().catch(() => []),
      apiGetPacks().catch(() => ({ data: [] })),
      apiGetCardAccounts().catch(() => ({ data: [] })),
      apiGetLeaderboard().catch(() => ({ data: [] })),
    ]).then(([cardsRaw, expsRaw, packsRaw, accountsRaw, lbRaw]) => {
      if (!isMounted) return;
      setCards(Array.isArray(cardsRaw) ? cardsRaw : (cardsRaw as any)?.data ?? []);
      setExpansions(Array.isArray(expsRaw) ? expsRaw : (expsRaw as any)?.data ?? []);
      setPacks(Array.isArray(packsRaw) ? packsRaw : (packsRaw as any)?.data ?? []);
      setAccounts(Array.isArray(accountsRaw) ? accountsRaw : (accountsRaw as any)?.data ?? []);
      setLeaderboard(Array.isArray(lbRaw) ? lbRaw : (lbRaw as any)?.data ?? []);
    }).finally(() => { if (isMounted) setIsLoading(false); });
    return () => { isMounted = false; };
  }, []);

  const rarityStats = useMemo(() => {
    return RARITIES.map((r) => {
      const count = cards.filter((c) => c.rarity === r).length;
      const pct = cards.length > 0 ? Math.round((count / cards.length) * 100) : 0;
      return { rarity: r, count, pct };
    });
  }, [cards]);

  const creditStats = useMemo(() => {
    const total = accounts.reduce((s, a) => s + (a.credits ?? 0), 0);
    const avg = accounts.length > 0 ? Math.round(total / accounts.length) : 0;
    return { total, avg };
  }, [accounts]);

  const activePacks = packs.filter((p) => (p as any).is_active).length;
  const top5 = leaderboard.slice(0, 5);

  if (isLoading) {
    return (
      <CommonWrapper>
        <PageInner>
          <PageTitle>Dashboard</PageTitle>
          <StatusText>Caricamento...</StatusText>
        </PageInner>
      </CommonWrapper>
    );
  }

  return (
    <CommonWrapper>
      <PageInner>
        <PageTitle>Dashboard</PageTitle>

        {/* KPI */}
        <Section>
          <SectionTitle>Panoramica</SectionTitle>
          <KpiGrid>
            <KpiCard $accent="gold">
              <KpiLabel>Carte nel catalogo</KpiLabel>
              <KpiValue $accent="gold">{cards.length}</KpiValue>
              <KpiSub>{rarityStats.find(r => r.rarity === 'legendary')?.count ?? 0} legendary</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Espansioni</KpiLabel>
              <KpiValue>{expansions.length}</KpiValue>
              <KpiSub>{expansions.filter((e) => (e as any).is_active).length} attive</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Pacchetti</KpiLabel>
              <KpiValue>{packs.length}</KpiValue>
              <KpiSub>{activePacks} attivi</KpiSub>
            </KpiCard>
            <KpiCard $accent="#4a9eff">
              <KpiLabel>Collezionisti</KpiLabel>
              <KpiValue $accent="#4a9eff">{accounts.length}</KpiValue>
              <KpiSub>con card account</KpiSub>
            </KpiCard>
          </KpiGrid>
        </Section>

        {/* Crediti */}
        <Section>
          <SectionTitle>Crediti in circolazione</SectionTitle>
          <CreditsGrid>
            <KpiCard $accent="gold">
              <KpiLabel>Totale crediti</KpiLabel>
              <KpiValue $accent="gold" style={{ fontSize: 26 }}>
                {creditStats.total.toLocaleString('it-IT')}
              </KpiValue>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Media per utente</KpiLabel>
              <KpiValue style={{ fontSize: 26 }}>
                {creditStats.avg.toLocaleString('it-IT')}
              </KpiValue>
            </KpiCard>
          </CreditsGrid>
        </Section>

        {/* Distribuzione rarità */}
        <Section>
          <SectionTitle>Distribuzione carte per rarità</SectionTitle>
          <RarityGrid>
            {rarityStats.map(({ rarity, count, pct }) => (
              <RarityCard key={rarity} $color={RARITY_COLOR[rarity]}>
                <RarityName $color={RARITY_COLOR[rarity]}>{rarity}</RarityName>
                <RarityCount>{count}</RarityCount>
                <RarityPct>{pct}% del catalogo</RarityPct>
                <RarityTrack>
                  <RarityFill $pct={pct} $color={RARITY_COLOR[rarity]} />
                </RarityTrack>
              </RarityCard>
            ))}
          </RarityGrid>
        </Section>

        {/* Top collezionisti */}
        <Section>
          <SectionTitle>Top collezionisti</SectionTitle>
          {top5.length === 0 ? (
            <StatusText>Nessun dato disponibile.</StatusText>
          ) : (
            <CollectorsTable>
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Utente</Th>
                  <ThRight>Carte uniche</ThRight>
                  <ThRight>Legendary</ThRight>
                  <ThRight>Copie totali</ThRight>
                </tr>
              </thead>
              <tbody>
                {top5.map((entry) => (
                  <Tr key={entry.username}>
                    <Td><RankBadge $rank={entry.rank}>#{entry.rank}</RankBadge></Td>
                    <Td>
                      <strong>{entry.username}</strong>
                      {entry.nickname_forum && (
                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginLeft: 6 }}>
                          {entry.nickname_forum}
                        </span>
                      )}
                    </Td>
                    <TdRight>{entry.unique_cards}</TdRight>
                    <TdRight>
                      {entry.legendary_count > 0
                        ? <LegBadge>{entry.legendary_count} ★</LegBadge>
                        : <span style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>
                      }
                    </TdRight>
                    <TdRight>{entry.total_copies}</TdRight>
                  </Tr>
                ))}
              </tbody>
            </CollectorsTable>
          )}
        </Section>

      </PageInner>
    </CommonWrapper>
  );
};

export default DashboardPage;
